import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardMedia,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Fade,
  LinearProgress,
  Button,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  ImageSearch as ImageSearchIcon,
  PhotoSizeSelectActual as SizeIcon,
  Download as DownloadIcon,
  AddToPhotos as AddToAlbumIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { searchImages, searchByImage, getThumbnailUrl, getAllAlbums, addImageToAlbum } from '../services/api';
import FacetedSearch from './FacetedSearch';
import DimensionSelector from './DimensionSelector';
import { groupImagesByBaseName, getRepresentativeImages } from '../utils/imageGrouping';
import { downloadImage } from '../utils/downloadUtils';

const MotionCard = motion(Card);

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [results, setResults] = useState([]);
  const [allResults, setAllResults] = useState([]); // Store unfiltered results
  const [displayResults, setDisplayResults] = useState([]); // Store grouped results for display
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  // Album management state
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [selectedAlbums, setSelectedAlbums] = useState([]);
  const [albumSearchQuery, setAlbumSearchQuery] = useState('');

  // Dimension selector state
  const [hoveredImage, setHoveredImage] = useState(null);
  const [selectedImageVariants, setSelectedImageVariants] = useState({});

  // Caching and navigation state
  const [searchCache, setSearchCache] = useState(() => {
    // Initialize cache from sessionStorage
    try {
      const stored = sessionStorage.getItem('searchCache');
      return stored ? new Map(JSON.parse(stored)) : new Map();
    } catch {
      return new Map();
    }
  });
  const [lastQuery, setLastQuery] = useState(() => {
    return sessionStorage.getItem('lastSearchQuery') || '';
  });
  const scrollPositionRef = useRef(parseInt(sessionStorage.getItem('searchScrollPosition') || '0', 10));
  const isRestoringFromCache = useRef(false);
  // Save scroll position before navigation
  const saveScrollPosition = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
    sessionStorage.setItem('searchScrollPosition', window.scrollY.toString());
  }, []);

  // Restore scroll position after results are loaded
  const restoreScrollPosition = useCallback(() => {
    const savedPosition = parseInt(sessionStorage.getItem('searchScrollPosition') || '0', 10);
    if (isRestoringFromCache.current && savedPosition > 0) {
      setTimeout(() => {
        window.scrollTo(0, savedPosition);
        isRestoringFromCache.current = false;
        sessionStorage.removeItem('searchScrollPosition'); // Clear after restore
      }, 100);
    }
  }, []);

  // Create cache key for search results
  const createCacheKey = useCallback((query, searchType = 'text') => {
    return `${searchType}:${query}`;
  }, []);

  // Check if we have cached results for this search
  const getCachedResults = useCallback((cacheKey) => {
    console.log('getCachedResults called with key:', cacheKey);
    console.log('searchCache contents:', Array.from(searchCache.entries()));

    // First check in-memory cache
    const memoryCache = searchCache.get(cacheKey);
    console.log('Memory cache result:', memoryCache);
    if (memoryCache) return memoryCache;

    // Then check sessionStorage
    try {
      const stored = sessionStorage.getItem('searchCache');
      console.log('SessionStorage raw value:', stored);
      if (stored) {
        const cache = new Map(JSON.parse(stored));
        console.log('Parsed sessionStorage cache:', Array.from(cache.entries()));
        const result = cache.get(cacheKey);
        console.log('SessionStorage cache result for key:', cacheKey, result);
        return result;
      }
    } catch (e) {
      console.error('Error reading from sessionStorage:', e);
    }
    console.log('No cache found for key:', cacheKey);
    return undefined;
  }, [searchCache]);

  // Cache search results
  const cacheSearchResults = useCallback((cacheKey, data) => {
    const cacheEntry = {
      ...data,
      timestamp: Date.now()
    };

    // Update in-memory cache
    const newCache = new Map(searchCache);
    newCache.set(cacheKey, cacheEntry);
    setSearchCache(newCache);

    // Persist to sessionStorage
    try {
      const cacheArray = Array.from(newCache.entries());
      sessionStorage.setItem('searchCache', JSON.stringify(cacheArray));
    } catch {
      // Ignore storage errors (quota exceeded, etc.)
    }
  }, [searchCache]);

  const performTextSearch = async (query, pageNum = 1, append = false) => {
    if (pageNum === 1) {
      setLoading(true);
      setResults([]);
      setAllResults([]);
      setDisplayResults([]);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const response = await searchImages(query, pageNum, 100);
      const newResults = response.results || [];

      let updatedAllResults;
      if (append) {
        updatedAllResults = [...allResults, ...newResults];
        setAllResults(updatedAllResults);
        setResults(prev => [...prev, ...newResults]);
      } else {
        updatedAllResults = newResults;
        setAllResults(updatedAllResults);
        setResults(updatedAllResults);
      }

      // Apply grouping to all results
      const grouped = groupImagesByBaseName(updatedAllResults);
      const representatives = getRepresentativeImages(grouped);
      setDisplayResults(representatives);

      setTotalResults(response.total || 0);

      // Calculate current total after this page
      const currentTotal = append ? allResults.length + newResults.length : newResults.length;
      setHasMore(newResults.length === 100 && currentTotal < response.total);
      setPage(pageNum);

      // Cache the results if this is a fresh search (not append)
      if (!append) {
        const cacheKey = createCacheKey(query);
        const cacheData = {
          allResults: updatedAllResults,
          results: updatedAllResults,
          displayResults: representatives,
          totalResults: response.total || 0,
          hasMore: newResults.length === 100 && currentTotal < response.total,
          page: pageNum
        };
        console.log('Caching search results for query:', query, 'key:', cacheKey, 'data:', cacheData);
        cacheSearchResults(cacheKey, cacheData);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const performImageSearch = async (imageFile) => {
    setLoading(true);
    setError(null);

    try {
      const response = await searchByImage(imageFile);
      setAllResults(response.results); // Store all results
      setResults(response.results); // Initially show all

      // Apply grouping to image search results
      const grouped = groupImagesByBaseName(response.results);
      const representatives = getRepresentativeImages(grouped);
      setDisplayResults(representatives);

      // Cache image search results - use unique key for each image
      const imageHash = imageFile.name + imageFile.size + imageFile.lastModified;
      const cacheKey = createCacheKey(`visual_search_${imageHash}`, 'image');
      cacheSearchResults(cacheKey, {
        allResults: response.results,
        results: response.results,
        displayResults: representatives,
        totalResults: response.results.length,
        hasMore: false,
        page: 1
      });
    } catch (err) {
      console.error('Image search error:', err);
      setError('Image search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const query = searchParams.get('q');
    const imageFile = location.state?.searchImage;

    console.log('useEffect triggered - query:', query, 'lastQuery:', lastQuery, 'imageFile:', !!imageFile);

    if (query) {
      setSearchQuery(query);

      // Check if this is the same query as before or a new one
      const isNewQuery = query !== lastQuery;

      if (isNewQuery) {
        console.log('Processing new query:', query, 'previous:', lastQuery);
        setLastQuery(query);
        sessionStorage.setItem('lastSearchQuery', query);
      } else {
        console.log('Same query as before, checking cache:', query);
      }

      setPage(1);

      // Always check cache first (for both new and returning queries)
      const cacheKey = createCacheKey(query);
      const cached = getCachedResults(cacheKey);

      if (cached && Date.now() - cached.timestamp < 300000) { // 5 minutes cache
        console.log('Using cached results for query:', query, cached);
        // Restore from cache
        isRestoringFromCache.current = true;
        setAllResults(cached.allResults);
        setResults(cached.results);
        setDisplayResults(cached.displayResults);
        setTotalResults(cached.totalResults);
        setHasMore(cached.hasMore);
        setPage(cached.page);
        setError(null);
        restoreScrollPosition();
      } else {
        console.log('No cache found for query:', query, 'cached:', cached, 'performing fresh search');
        // Perform fresh search
        performTextSearch(query, 1, false);
      }
    } else if (imageFile) {
      setSearchQuery('Visual search');

      // Check cache for image search - use unique key for each image
      const imageHash = imageFile.name + imageFile.size + imageFile.lastModified;
      const cacheKey = createCacheKey(`visual_search_${imageHash}`, 'image');
      const cached = getCachedResults(cacheKey);

      if (cached && Date.now() - cached.timestamp < 300000) { // 5 minutes cache
        // Restore from cache
        isRestoringFromCache.current = true;
        setAllResults(cached.allResults);
        setResults(cached.results);
        setDisplayResults(cached.displayResults);
        setTotalResults(cached.totalResults);
        setHasMore(cached.hasMore);
        setPage(cached.page);
        setError(null);
        restoreScrollPosition();
      } else {
        performImageSearch(imageFile);
      }
    }

    // Set up scroll position saving on navigation
    const saveBeforeUnload = () => saveScrollPosition();
    window.addEventListener('beforeunload', saveBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', saveBeforeUnload);
    };
  }, [searchParams, location.state]);

  // Restore scroll position when results are loaded from cache
  useEffect(() => {
    if (displayResults.length > 0) {
      restoreScrollPosition();
    }
  }, [displayResults, restoreScrollPosition]);

  // Apply filters when they change
  useEffect(() => {
    if (allResults.length === 0) return;

    let filteredResults = [...allResults];

    // Filter by file type
    if (filters.fileType && filters.fileType.length > 0) {
      filteredResults = filteredResults.filter(result => {
        const fileType = filters.fileType[0];
        if (fileType === 'image') {
          return result.content_type?.startsWith('image/') ||
                 !result.filename?.match(/\.(eps|svg|mp4|avi|mov)$/i);
        } else if (fileType === 'vector') {
          return result.filename?.match(/\.(eps|svg)$/i);
        } else if (fileType === 'video') {
          return result.content_type?.startsWith('video/') ||
                 result.filename?.match(/\.(mp4|avi|mov|webm)$/i);
        }
        return true;
      });
    }

    // Filter by stock source
    if (filters.stockSource && filters.stockSource.length > 0) {
      filteredResults = filteredResults.filter(result => {
        return filters.stockSource.includes(result.stock_source);
      });
    }

    // Filter by people count
    if (filters.peopleCount && filters.peopleCount.length > 0) {
      filteredResults = filteredResults.filter(result => {
        const peopleFilter = filters.peopleCount[0];
        const faceCount = result.google_vision_faces || 0;

        switch(peopleFilter) {
          case 'none':
            return faceCount === 0;
          case 'one':
            return faceCount === 1;
          case 'two':
            return faceCount === 2;
          case '3':
            return faceCount === 3;
          case '4':
            return faceCount === 4;
          case '5':
            return faceCount === 5;
          case '5+':
            return faceCount >= 5;
          default:
            return true;
        }
      });
    }

    setResults(filteredResults);

    // Apply grouping to filtered results
    const grouped = groupImagesByBaseName(filteredResults);
    const representatives = getRepresentativeImages(grouped);
    setDisplayResults(representatives);
  }, [filters, allResults]);

  const handleImageClick = (imageId) => {
    // Save scroll position before navigating
    saveScrollPosition();
    navigate(`/image/${imageId}`);
  };

  // Handle image variant selection
  const handleImageVariantSelect = (baseName, selectedImage) => {
    setSelectedImageVariants(prev => ({
      ...prev,
      [baseName]: selectedImage
    }));
  };

  // Get the currently selected image for a group, or the representative image
  const getCurrentImageForGroup = (imageGroup) => {
    const baseName = imageGroup.baseName;
    return selectedImageVariants[baseName] || imageGroup;
  };

  // Handle image download
  const handleDownloadImage = async (image, event) => {
    event.stopPropagation();
    await downloadImage(image, null, () => {
      setError('Failed to download image. Please try again.');
    });
  };

  // Album management functions
  const handleAddToAlbum = async (imageId, event) => {
    event.stopPropagation();
    handleAlbumDialogOpen(imageId);
  };

  const handleAlbumToggle = (albumId) => {
    setSelectedAlbums(prev => {
      if (prev.includes(albumId)) {
        return prev.filter(id => id !== albumId);
      }
      return [...prev, albumId];
    });
  };

  const handleSaveToAlbums = async () => {
    if (!selectedImage || selectedAlbums.length === 0) return;

    try {
      // Add image to each selected album
      await Promise.all(
        selectedAlbums.map(albumId => addImageToAlbum(albumId, selectedImage))
      );

      setAlbumDialogOpen(false);
      setSelectedImage(null);
      setSelectedAlbums([]);
      // Optionally show success message
    } catch (err) {
      console.error('Error adding to albums:', err);
      setError('Failed to add image to albums.');
    }
  };

  // Clear search when dialog opens
  const handleAlbumDialogOpen = (imageId) => {
    setSelectedImage(imageId);
    setAlbumSearchQuery(''); // Clear search when opening

    // Load albums if not already loaded
    if (albums.length === 0) {
      loadAlbums();
    }

    setAlbumDialogOpen(true);
  };

  const loadAlbums = async () => {
    try {
      const response = await getAllAlbums();
      setAlbums(response.albums);
    } catch (err) {
      console.error('Error loading albums:', err);
      setError('Failed to load albums.');
    }
  };

  // Filter albums based on search query
  const filteredAlbums = useMemo(() => {
    if (!albumSearchQuery.trim()) {
      return albums;
    }

    const query = albumSearchQuery.toLowerCase().trim();
    return albums.filter(album =>
      album.name.toLowerCase().includes(query) ||
      (album.description && album.description.toLowerCase().includes(query))
    );
  }, [albums, albumSearchQuery]);

  // Helper function to highlight search matches
  const highlightSearchMatch = (text, query) => {
    if (!query.trim()) return text;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === query.toLowerCase()) {
        return (
          <Box
            key={index}
            component="span"
            sx={{
              backgroundColor: 'rgba(102, 126, 234, 0.3)',
              fontWeight: 'bold',
              padding: '0 2px',
              borderRadius: '2px'
            }}
          >
            {part}
          </Box>
        );
      }
      return part;
    });
  };

  const getSearchTypeIcon = () => {
    return location.state?.searchImage ? <ImageSearchIcon /> : <SearchIcon />;
  };

  const getSearchTypeLabel = () => {
    return location.state?.searchImage ? 'Visual Search' : 'Search Results';
  };

  return (
    <Box>
      {/* Faceted Search Sidebar - Fixed Position */}
      {(allResults.length > 0 || loading) && (
        <FacetedSearch
          filters={filters}
          onFiltersChange={setFilters}
          resultCount={allResults.length} // Show total count, not filtered
        />
      )}

      {/* Main Content Area with Left Margin */}
      <Box sx={{ ml: (allResults.length > 0 || loading) ? '292px' : 0, pr: 2, pl: 1 }}>
        {/* Search Header */}
        <Paper
          sx={{
            p: 3,
            mb: 4,
            background: 'rgba(26, 26, 46, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {getSearchTypeIcon()}
          <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
            {getSearchTypeLabel()}
          </Typography>
        </Box>

        <Typography
          variant="h6"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            mb: 1,
            fontStyle: 'italic',
          }}
        >
          "{searchQuery}"
        </Typography>

        <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          {loading ? 'Searching...' : totalResults > 0 ?
            `Showing ${displayResults.length} unique images (${results.length} total files) of ${totalResults} result${totalResults !== 1 ? 's' : ''}` :
            `Found ${displayResults.length} unique images${displayResults.length !== results.length ? ` (${results.length} total files)` : ''}`
          }
        </Typography>

        {loading && (
          <LinearProgress
            sx={{
              mt: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
              }
            }}
          />
        )}
      </Paper>

      {/* Loading State */}
      {loading && results.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="40vh"
          gap={2}
        >
          <CircularProgress size={60} sx={{ color: '#667eea' }} />
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            AI is analyzing your search...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            {error}
          </Alert>
        </Box>
      )}

      {/* No Results */}
      {!loading && displayResults.length === 0 && !error && (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'rgba(26, 26, 46, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <SearchIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
            No Results Found
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Try using different keywords or search terms.
            The AI search works best with descriptive phrases like "red car", "sunset beach", or "happy family".
          </Typography>
        </Paper>
      )}

        {/* Results Grid */}
        {displayResults.length > 0 && (
          <Grid container spacing={2}>
            {displayResults.map((result, index) => {
              const currentImage = getCurrentImageForGroup(result);
              return (
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2.4} key={result.id || index}>
              <Fade in timeout={300 + index * 50}>
                <MotionCard
                  whileHover={{
                    scale: 1.03,
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                  }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    cursor: 'pointer',
                    background: 'rgba(26, 26, 46, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onClick={() => handleImageClick(currentImage.id)}
                  onMouseEnter={() => setHoveredImage(result.baseName)}
                  onMouseLeave={() => setHoveredImage(null)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="160"
                      image={getThumbnailUrl(currentImage.thumbnail_path, currentImage.thumbnail_s3_url, currentImage.id) || '/placeholder-image.jpg'}
                      alt={currentImage.description || 'Search result'}
                      sx={{
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                        }
                      }}
                    />

                    {/* Relevance Score */}
                    {currentImage.score && (
                      <Chip
                        label={`${Math.round(currentImage.score * 100)}% match`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          backgroundColor: currentImage.score > 0.8
                            ? 'rgba(76, 175, 80, 0.9)'
                            : currentImage.score > 0.6
                              ? 'rgba(255, 193, 7, 0.9)'
                              : 'rgba(244, 67, 54, 0.9)',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    )}

                    {/* Match Type */}
                    {currentImage.match_type && (
                      <Chip
                        label={currentImage.match_type}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(102, 126, 234, 0.9)',
                          color: 'white',
                          fontSize: '0.7rem',
                        }}
                      />
                    )}

                    {/* Multi-size indicator */}
                    {result.isGrouped && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          left: 8,
                          zIndex: 1
                        }}
                      >
                        <Badge
                          badgeContent={result.variantCount}
                          color="primary"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.7rem',
                              minWidth: 16,
                              height: 16
                            }
                          }}
                        >
                          <Chip
                            icon={<SizeIcon />}
                            label="sizes"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 20,
                              '& .MuiChip-icon': {
                                fontSize: '0.8rem',
                                color: 'white'
                              }
                            }}
                          />
                        </Badge>
                      </Box>
                    )}

                    {/* Action buttons */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        display: 'flex',
                        gap: 1,
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        '.MuiCard-root:hover &': {
                          opacity: 1,
                        }
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.8)',
                          }
                        }}
                        onClick={(e) => handleAddToAlbum(currentImage.id, e)}
                      >
                        <AddToAlbumIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(33, 150, 243, 0.8)',
                          }
                        }}
                        onClick={(e) => handleDownloadImage(currentImage, e)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Dimension Selector Overlay */}
                    {result.isGrouped && hoveredImage === result.baseName && (
                      <DimensionSelector
                        imageGroup={result.groupVariants}
                        selectedImage={currentImage}
                        onImageSelect={(selectedImage) => handleImageVariantSelect(result.baseName, selectedImage)}
                        onDownloadError={() => setError('Failed to download image. Please try again.')}
                        showInline={true}
                      />
                    )}
                  </Box>

                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '0.875rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {currentImage.description || 'No description available'}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {(currentImage.google_vision_labels || currentImage.tags || [])?.slice(0, 3).map((item, idx) => {
                        // Handle both old format (strings) and new AI format (objects)
                        const labelText = typeof item === 'string' ? item : item?.name || item;
                        const confidence = typeof item === 'object' ? item?.confidence : null;

                        return (
                          <Chip
                            key={idx}
                            label={confidence ? `${labelText} (${Math.round(confidence * 100)}%)` : labelText}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: 24,
                              backgroundColor: confidence ? 'rgba(76, 175, 80, 0.2)' : 'rgba(102, 126, 234, 0.2)',
                              color: 'rgba(255, 255, 255, 0.8)',
                              border: confidence ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(102, 126, 234, 0.3)',
                            }}
                          />
                        );
                      })}
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {currentImage.dimensions ? `${currentImage.dimensions[0]}×${currentImage.dimensions[1]}` :
                       currentImage.width && currentImage.height ? `${currentImage.width}×${currentImage.height}` : 'AI analyzed'}
                      {result.isGrouped && ` • ${result.variantCount} sizes`}
                      {currentImage.google_vision_faces > 0 && ` • ${currentImage.google_vision_faces} face${currentImage.google_vision_faces > 1 ? 's' : ''}`}
                      {currentImage.score && ` • ${Math.round(currentImage.score * 100)}% match`}
                    </Typography>
                  </Box>
                </MotionCard>
              </Fade>
            </Grid>
              );
            })}
          </Grid>
        )}

        {/* Load More Button */}
        {hasMore && !loading && displayResults.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
            <Button
              variant="contained"
              onClick={() => performTextSearch(searchQuery, page + 1, true)}
              disabled={loadingMore}
              sx={{
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                color: 'white',
                px: 4,
                py: 1.5,
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a67d8, #6b46c1)',
                },
              }}
            >
              {loadingMore ? (
                <>
                  <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                  Loading...
                </>
              ) : (
                `Load More (${displayResults.length} unique of ${totalResults} total)`
              )}
            </Button>
          </Box>
        )}
      </Box>

      {/* Album Selection Dialog */}
      <Dialog
        open={albumDialogOpen}
        onClose={() => setAlbumDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle sx={{ color: '#ffffff' }}>Add to Albums</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
            Select albums to add this image to:
          </Typography>

          {/* Search Input */}
          <TextField
            fullWidth
            placeholder="Search albums..."
            value={albumSearchQuery}
            onChange={(e) => setAlbumSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setAlbumSearchQuery('');
                e.target.blur();
              }
            }}
            variant="outlined"
            size="small"
            autoFocus
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#ffffff',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                </InputAdornment>
              ),
              endAdornment: albumSearchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setAlbumSearchQuery('')}
                    sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Search Results Indicator */}
          {albumSearchQuery && (
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                mb: 1,
                display: 'block'
              }}
            >
              Showing {filteredAlbums.length} of {albums.length} albums
            </Typography>
          )}
          <List sx={{ width: '100%', minWidth: 300 }}>
            {filteredAlbums.map((album) => (
              <ListItem key={album.id} disablePadding>
                <ListItemButton
                  onClick={() => handleAlbumToggle(album.id)}
                  sx={{ borderRadius: 1 }}
                >
                  <Checkbox
                    checked={selectedAlbums.includes(album.id)}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      '&.Mui-checked': {
                        color: '#667eea',
                      },
                    }}
                  />
                  <ListItemText
                    primary={highlightSearchMatch(album.name, albumSearchQuery)}
                    secondary={`${album.image_count} images`}
                    primaryTypographyProps={{
                      color: '#ffffff'
                    }}
                    secondaryTypographyProps={{
                      color: 'rgba(255, 255, 255, 0.5)'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {albums.length === 0 && (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center', py: 2 }}>
              No albums available. Create an album first.
            </Typography>
          )}

          {albums.length > 0 && filteredAlbums.length === 0 && (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center', py: 2 }}>
              No albums match your search "{albumSearchQuery}".
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlbumDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveToAlbums}
            variant="contained"
            disabled={selectedAlbums.length === 0}
            sx={{
              backgroundColor: '#667eea',
              '&:hover': {
                backgroundColor: '#764ba2',
              },
            }}
          >
            Add to {selectedAlbums.length} Album{selectedAlbums.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SearchResults;
